// Categorical palette for user-supplied points: Kate Morley's 12-bit rainbow
// (https://iamkate.com/data/12-bit-rainbow/), given here as 6-digit hex so the values survive
// helpers that slice a hex string. Deliberately NOT in the published rainbow order — that
// puts neighbouring hues in consecutive slots, which is right for a sequence but not for
// telling points apart. Reordered for maximum separation, the first five slots clear both
// separation floors on a white plot surface (worst CVD deltaE 9.1, normal-vision 17.7);
// past five, points lean on their labels and the legend to carry identity. The lighter
// slots sit below 3:1 contrast against white, which the point labels likewise cover.
export const USER_DATA_COLOR_PALETTE = [
    '#881177', // 12-bit #817, purple
    '#eedd00', // 12-bit #ed0, yellow
    '#0099cc', // 12-bit #09c, blue
    '#cc6666', // 12-bit #c66, red
    '#44dd88', // 12-bit #4d8, green
    '#3366bb', // 12-bit #36b, indigo
    '#ee9944', // 12-bit #e94, orange
    '#aa3355', // 12-bit #a35, maroon
    '#00bbcc', // 12-bit #0bc, cyan
    '#663399', // 12-bit #639, violet
    '#99dd55', // 12-bit #9d5, lime
    '#22ccbb', // 12-bit #2cb, teal
];

// The colour(s) the API hands to a benchmark point that has never been styled. A point
// carrying one of these is unstyled and takes a palette colour; anything else is the user's
// own choice and is left alone. Taken from crossPlotPointOptions[].stringColor on unstyled
// points in /BenchmarkPlot/{id}/full. (#000080 is the globalData series colour, not this.)
export const BENCHMARK_DEFAULT_POINT_COLORS: string[] = ['#0000FF'];

const normaliseColor = (color?: string | null) => typeof color === 'string' ? color.trim().toLowerCase() : '';

// `colorIndex` is a series-level array running parallel to ids/markers/crossPlotPointOptions —
// the slot the API has reserved for each record in this workspace, stable across loads. Prefer
// it to the point's position in the response: position shifts whenever a record is added,
// removed, deselected or filtered out, and every colour after it moves with it. Empty on
// globalData series, which are styled series-wide rather than per point.
// Returns null when the payload doesn't carry one, so callers can fall back to position.
const getPointColorIndex = (series: { colorIndex?: (number | null)[] } | null | undefined, index: number): number | null => {
    const slot = series?.colorIndex?.[index];
    return typeof slot === 'number' && Number.isFinite(slot) ? slot : null;
};

// Benchmark user points arrive from the API sharing a single default colour, which makes
// every input look the same on the plot. Spread them across the palette so each input is
// identifiable, keeping any point the user has explicitly restyled on its own colour.
// Returns one colour array per userData series, aligned with that series' point order.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getBenchmarkUserDataColors = (userData: any[] | null | undefined): string[][] => {
    if (!Array.isArray(userData)) return [];

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const savedColors = userData.map((series: any) => {
        const length = series?.ids?.length ?? series?.x?.length ?? 0;
        return Array.from({ length }, (_, i) =>
            series?.crossPlotPointOptions?.[i]?.stringColor || series?.markers?.[i]?.color || '');
    });

    const knownDefaults = new Set(BENCHMARK_DEFAULT_POINT_COLORS.map(normaliseColor).filter(Boolean));
    let isDefaultColor = (key: string) => knownDefaults.has(key);

    if (!knownDefaults.size) {
        // Fallback while BENCHMARK_DEFAULT_POINT_COLORS is unset: assume the colour shared by
        // the most points is the API default, and that a colour used by a single point is a
        // deliberate choice. This cannot tell an unstyled set apart from one the user has
        // deliberately painted a single colour via the style panel's "all colour" option, so
        // that choice gets overridden until the default colour above is filled in.
        const counts = new Map<string, number>();
        savedColors.flat().forEach(color => {
            const key = normaliseColor(color);
            if (key) counts.set(key, (counts.get(key) ?? 0) + 1);
        });
        let defaultColor = '';
        let highestCount = 1;
        counts.forEach((count, color) => {
            if (count > highestCount) {
                highestCount = count;
                defaultColor = color;
            }
        });
        isDefaultColor = (key: string) => key === defaultColor;
    }

    let pointIndex = 0;
    return savedColors.map((series, seriesIndex) => series.map((color, index) => {
        const slot = getPointColorIndex(userData[seriesIndex], index) ?? pointIndex;
        const paletteColor = USER_DATA_COLOR_PALETTE[slot % USER_DATA_COLOR_PALETTE.length];
        pointIndex += 1;
        const key = normaliseColor(color);
        return !key || isDefaultColor(key) ? paletteColor : color;
    }));
};

// The red, green and blue channels of a colour, or null if it is not in a form we read.
// Both forms turn up: the palettes and constants above are hex, while anything the user has
// picked comes back from the API as the `rgba(r,g,b,a)` the style popups save. Alpha is
// discarded - every caller here is reasoning about hue, not opacity.
const toChannels = (color?: string | null): [number, number, number] | null => {
    if (typeof color !== 'string') return null;

    const trimmed = color.trim();

    const numeric = trimmed.match(/^rgba?\(([^)]+)\)$/i);
    if (numeric) {
        const channels = numeric[1].split(',').map(value => parseInt(value, 10));
        return channels.length >= 3 && channels.slice(0, 3).every(c => Number.isFinite(c))
            ? [channels[0], channels[1], channels[2]]
            : null;
    }

    const hex = trimmed.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i)?.[1];
    if (!hex) return null;

    // #abc is #aabbcc, so each digit doubles before it is read as a byte.
    const full = hex.length === 3 ? hex.replace(/./g, digit => digit + digit) : hex;
    const value = parseInt(full, 16);

    return [(value >> 16) & 0xff, (value >> 8) & 0xff, value & 0xff];
};

// A colour blended toward white, returned as hex. `amount` is how far: 0 returns the colour,
// 1 returns white. A colour we cannot read is passed back untouched.
export const lighten = (color: string, amount: number) => {
    const channels = toChannels(color);
    if (!channels) return color;

    const blend = (channel: number) => Math.round(channel + (255 - channel) * amount);
    const [r, g, b] = channels.map(blend);

    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

// How far the imputed hatching's background is lightened from the series colour. Held here as one
// value because the plot and the legend chip stripe through different engines - plotly's pattern
// fill and a CSS gradient - and two copies of it would drift apart unnoticed.
export const IMPUTED_HATCH_BLEND = 0.65;

// How far a grid cell's background is lightened from the same colour. Far paler than the hatching,
// because a cell has the value written across it and the number has to stay easy to read.
export const IMPUTED_CELL_BLEND = 0.85;

const IMPUTED_HATCH_TILE = 9;
// Share of the swatch covered by the series colour. A half makes the coloured and the pale stripe
// the same width. Not weighted for legibility any more - the label no longer sits on the stripes.
const IMPUTED_HATCH_SOLIDITY = 0.5;

// The legend's answer to plotly's `/` pattern fill, for the swatch that keys a hatched series. An
// SVG pattern rather than the repeating-linear-gradient this used to be: a gradient's hard stops get
// no antialiasing across a diagonal, which at this size reads as blotches rather than stripes.
// Plotly draws the bars from an SVG pattern too, which is why they look clean at the same tile size.
export const hatchedSwatch = (color: string) => {
    const spacing = IMPUTED_HATCH_TILE / Math.SQRT2;
    const strokeWidth = (IMPUTED_HATCH_SOLIDITY * spacing).toFixed(2);
    // Three lines, not one: the tile repeats on both axes, so the neighbours' stripes have to be
    // drawn to cover the corners this tile's own stripe misses.
    const stripes = [0, IMPUTED_HATCH_TILE, IMPUTED_HATCH_TILE * 2]
        .map(offset => `M${-IMPUTED_HATCH_TILE},${offset + IMPUTED_HATCH_TILE}L${IMPUTED_HATCH_TILE * 2},${offset - IMPUTED_HATCH_TILE * 2}`)
        .join('');

    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${IMPUTED_HATCH_TILE}" height="${IMPUTED_HATCH_TILE}">`
        + `<rect width="${IMPUTED_HATCH_TILE}" height="${IMPUTED_HATCH_TILE}" fill="${lighten(color, IMPUTED_HATCH_BLEND)}"/>`
        + `<path d="${stripes}" stroke="${color}" stroke-width="${strokeWidth}"/>`
        + `</svg>`;

    return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
};

export const checkDarkOrLight = (color: string) => {
    const channels = toChannels(color)
    if (!channels) return 'dark'
    const [r, g, b] = channels
    const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b; // per ITU-R BT.709
    return luma < 160 ? 'light' : 'dark';
}